import { codeToText } from "@common/helpers/accounting";
import { ChartOfAccountsTemplateVariableKind } from "@common/constants/domains/accounting/chartOfAccountsTemplateAccount";
import { ChartOfAccountsTemplateAccountsData } from "@api/coaTemplate.api";
import { DraftAccount, DraftGroup } from "../draftOperations";

export const TEMPLATE_DRAFT_ENTITY_ID = 0;

// Off-tree template identity for a row: the columns the editor body no longer
// carries but the bulk-save payload still needs. Round-tripped in the host's
// side map, keyed by account id.
export interface TemplateAccountIdentity {
    variableKind: ChartOfAccountsTemplateVariableKind | null;
    kickOriginalCode: number | null;
}

export const NULL_TEMPLATE_IDENTITY: TemplateAccountIdentity = {
    variableKind: null,
    kickOriginalCode: null,
};

// Re-derived from every server payload the host maps. A missing entry (a
// freshly-created temp row) resolves to the null identity, which is exactly what
// the draft operations write for new rows.
export const buildTemplateIdentityMap = (
    serverData: ChartOfAccountsTemplateAccountsData,
): Map<string, TemplateAccountIdentity> =>
    new Map(
        serverData.accounts.map((account) => [
            account.id,
            {
                variableKind: account.variableKind,
                kickOriginalCode: account.kickOriginalCode,
            },
        ]),
    );

// Maps server accounts onto the host-agnostic DraftAccount. `preferCodeText` is
// inapplicable: it falls back only on `undefined`, and codeText is a nullable
// string on the read schema, so a null server codeText is derived from `code`.
export const mapServerToDraftAccounts = (
    serverData: ChartOfAccountsTemplateAccountsData,
): DraftAccount[] =>
    serverData.accounts.map((account) => ({
        id: account.id,
        entityId: TEMPLATE_DRAFT_ENTITY_ID,
        codeText: account.codeText ?? codeToText(account.code),
        name: account.name,
        type: account.type,
        isDisabled: account.isDisabled,
        isManuallyCreated: account.isManuallyCreated,
        role: account.role,
        form1099Box: account.form1099Box ?? null,
        parentGroupId: account.parentGroupId,
        sortOrder: account.sortOrder,
    }));

export const mapServerToDraftGroups = (
    serverData: ChartOfAccountsTemplateAccountsData,
): DraftGroup[] =>
    serverData.groups.map((group) => ({
        id: group.id,
        entityId: TEMPLATE_DRAFT_ENTITY_ID,
        name: group.name,
        type: group.type,
        parentGroupId: group.parentGroupId,
        sortOrder: group.sortOrder,
    }));
